import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { analytics } from '../utils/analytics';
import { Toast } from './Toast';

type Status = 'crediting' | 'success' | 'cancel' | null;

const CREDITING_MESSAGE = 'Processing your purchase…';
const SUCCESS_MESSAGE = "Tokens added to your balance. You're all set.";
const CANCEL_MESSAGE = 'Purchase canceled. No charge was made.';
const TOAST_AUTO_DISMISS_MS = 5000;
// Stripe webhook → D1 update can lag a few seconds after redirect-back.
// Poll for up to 12s waiting for token_balance to increase before falling
// back to the optimistic success message.
const CREDIT_POLL_TOTAL_MS = 12_000;
const CREDIT_POLL_INTERVAL_MS = 1500;

/**
 * Catches `?checkout=success|cancel` on ANY route (Stripe's success_url sends
 * users back to `/` by default, but the handler that refreshes their balance
 * only lived on `/app`, so purchases looked like a no-op to the user).
 *
 * On success: refresh session (so token_balance is current), fire analytics,
 * redirect to /app so the user lands where tokens get spent.
 * On cancel: strip the param in place, show a brief toast.
 */
export function CheckoutResultHandler() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState<Status>(null);
  // Guards StrictMode double-invoke and re-fires from navigate(replace).
  const seenRef = useRef<Set<string>>(new Set());
  // Snapshot the pre-redirect balance so we can detect when the webhook
  // has settled (token_balance increased server-side).
  const balanceBeforeRef = useRef<number | null>(null);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout !== 'success' && checkout !== 'cancel') return;
    if (seenRef.current.has(checkout)) return;
    seenRef.current.add(checkout);

    const remaining = new URLSearchParams(searchParams);
    remaining.delete('checkout');
    const qs = remaining.toString();
    const querySuffix = qs ? `?${qs}` : '';

    if (checkout === 'success') {
      // Show "Processing your purchase…" immediately so the user knows the
      // app saw their return - refreshUser is async and the webhook may
      // not have credited yet.
      balanceBeforeRef.current = user?.token_balance ?? null;
      analytics.purchaseComplete();
      setStatus('crediting');
      navigate(`/app${querySuffix}`, { replace: true });

      // Poll refreshUser until token_balance increases or we time out.
      // The Stripe webhook → D1 update can lag a few seconds; without
      // this poll the user sees their pre-purchase balance and panics.
      const startedAt = Date.now();
      const pollHandle = window.setInterval(async () => {
        await refreshUser();
        // Stop on timeout regardless. The webhook will eventually credit;
        // a brief mismatch is better than spinning forever.
        if (Date.now() - startedAt >= CREDIT_POLL_TOTAL_MS) {
          window.clearInterval(pollHandle);
          setStatus('success');
        }
      }, CREDIT_POLL_INTERVAL_MS);
      // Also fire one immediate refresh - most webhooks settle in <1s, no
      // reason to wait the first poll interval.
      refreshUser();
      return () => window.clearInterval(pollHandle);
    } else {
      setStatus('cancel');
      navigate(`${location.pathname}${querySuffix}`, { replace: true });
    }
  }, [searchParams, location.pathname, navigate, refreshUser, user?.token_balance]);

  // Flip from "crediting" to "success" the moment we observe an increased
  // balance. Separate effect so it tracks the user object across renders.
  useEffect(() => {
    if (status !== 'crediting') return;
    const before = balanceBeforeRef.current;
    const after = user?.token_balance ?? null;
    if (before !== null && after !== null && after > before) {
      setStatus('success');
    }
  }, [user?.token_balance, status]);

  if (!status) return null;
  const message =
    status === 'crediting' ? CREDITING_MESSAGE :
    status === 'success' ? SUCCESS_MESSAGE :
    CANCEL_MESSAGE;
  const kind: 'info' | 'success' | 'warning' =
    status === 'crediting' ? 'info' :
    status === 'success' ? 'success' :
    'warning';
  return (
    <Toast
      kind={kind}
      message={message}
      // Don't auto-dismiss while we're still polling - only the final
      // success / cancel state should fade.
      onDismiss={status === 'crediting' ? undefined : () => setStatus(null)}
      autoDismissMs={status === 'crediting' ? undefined : TOAST_AUTO_DISMISS_MS}
    />
  );
}
